import { useEffect, type ReactNode } from "react";
import { clsx } from "clsx";
import { CheckCircle, XCircle, AlertTriangle, Info, X } from "lucide-react";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  message: string;
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

const variantConfig: Record<ToastVariant, { icon: ReactNode; classes: string }> = {
  success: {
    icon: <CheckCircle className="h-5 w-5 text-[#16a34a]" />,
    classes: "bg-[#f0fdf4] border-[#16a34a]/20",
  },
  error: {
    icon: <XCircle className="h-5 w-5 text-[#dc2626]" />,
    classes: "bg-[#fef2f2] border-[#dc2626]/20",
  },
  warning: {
    icon: <AlertTriangle className="h-5 w-5 text-[#d97706]" />,
    classes: "bg-[#fffbeb] border-[#d97706]/20",
  },
  info: {
    icon: <Info className="h-5 w-5 text-[#0891b2]" />,
    classes: "bg-[#ecfeff] border-[#0891b2]/20",
  },
};

function Toast({ toast, onDismiss }: ToastProps) {
  const config = variantConfig[toast.variant];

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), 3000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <div
      className={clsx(
        "flex items-start gap-3 rounded-xl border px-4 py-3 shadow-[0_4px_16px_0_rgb(0_0_0/0.12)] min-w-[280px] max-w-sm",
        config.classes,
      )}
      role="alert"
    >
      <div className="shrink-0 mt-0.5">{config.icon}</div>
      <p className="flex-1 text-sm text-[#0f172a]">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 text-[#94a3b8] hover:text-[#64748b] transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
